import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * WorkflowDeploymentPanel
 *
 * Deployment management panel for workflows. Provides deploy with validation gate,
 * deployment status tracking, rollback with confirmation, and error display.
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7
 */
import { useState, useEffect, useCallback } from 'react';
import { useWorkflowBuilder } from '@/contexts/WorkflowBuilderContext';
import { workflowBuilderApi } from '@/api/workflowBuilderApi';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { StatusBadge } from '@/components/shared/StatusBadge';
const statusBadgeMap = {
    pending: 'warning',
    in_progress: 'open',
    completed: 'active',
    failed: 'error',
};
const WorkflowDeploymentPanel = ({ workflowId, onClose }) => {
    const { state } = useWorkflowBuilder();
    const [deployments, setDeployments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deploying, setDeploying] = useState(false);
    const [error, setError] = useState(null);
    const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);
    const [showDeployConfirm, setShowDeployConfirm] = useState(false);
    const [changeDescription, setChangeDescription] = useState('');
    const hasValidationErrors = state.validationErrors.errors.length > 0;
    const currentVersion = state.currentWorkflow?.uiMetadata?.version ?? 1;
    const activeDeployment = deployments.find((d) => d.status === 'completed');
    const loadDeployments = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await workflowBuilderApi.getDeploymentStatus(workflowId);
            setDeployments(data);
        }
        catch (err) {
            setError(err.response?.data?.message || 'Failed to load deployments');
        }
        finally {
            setLoading(false);
        }
    }, [workflowId]);
    useEffect(() => {
        void loadDeployments();
    }, [loadDeployments]);
    const handleDeploy = useCallback(async () => {
        if (hasValidationErrors)
            return;
        try {
            setDeploying(true);
            setError(null);
            await workflowBuilderApi.deployWorkflow({
                workflowId,
                version: currentVersion,
                environment: 'production',
                changeDescription: changeDescription || `Deploy version ${currentVersion}`,
            });
            setChangeDescription('');
            setShowDeployConfirm(false);
            await loadDeployments();
        }
        catch (err) {
            setError(err.response?.data?.message || 'Deployment failed');
        }
        finally {
            setDeploying(false);
        }
    }, [workflowId, currentVersion, changeDescription, hasValidationErrors, loadDeployments]);
    const handleRollback = useCallback(async () => {
        try {
            setDeploying(true);
            setError(null);
            await workflowBuilderApi.rollbackWorkflow(workflowId);
            setShowRollbackConfirm(false);
            await loadDeployments();
        }
        catch (err) {
            setError(err.response?.data?.message || 'Rollback failed');
        }
        finally {
            setDeploying(false);
        }
    }, [workflowId, loadDeployments]);
    if (loading) {
        return (_jsx("div", { className: "p-6 flex items-center justify-center", children: _jsx(LoadingSpinner, { size: "lg" }) }));
    }
    return (_jsxs("div", { className: "p-6 space-y-6", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("h2", { className: "text-lg font-semibold text-gray-900", children: "Deployment" }), onClose && (_jsx("button", { onClick: onClose, className: "text-gray-400 hover:text-gray-600", "aria-label": "Close deployment panel", children: "\u2715" }))] }), error && _jsx(ErrorAlert, { message: error, onDismiss: () => setError(null) }), _jsxs("div", { className: "bg-gray-50 rounded-lg p-4 space-y-2", children: [_jsx("p", { className: "text-sm font-medium text-gray-700", children: "Current Version" }), _jsxs("p", { className: "text-2xl font-bold text-gray-900", children: ["v", currentVersion] }), activeDeployment && (_jsxs("div", { className: "flex items-center gap-2 text-sm text-gray-600", children: [_jsx(StatusBadge, { status: statusBadgeMap[activeDeployment.status] || 'active', label: "Active" }), _jsxs("span", { children: ["Deployed ", new Date(activeDeployment.startedAt).toLocaleDateString()] })] }))] }), hasValidationErrors && (_jsxs("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4", children: [_jsx("p", { className: "text-sm font-medium text-red-800", children: "Cannot deploy \u2014 validation errors" }), _jsxs("p", { className: "text-xs text-red-600 mt-1", children: ["Fix ", state.validationErrors.errors.length, " error", state.validationErrors.errors.length !== 1 ? 's' : '', " before deploying."] })] })), _jsxs("div", { className: "space-y-3", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700", children: "Change description" }), _jsx("textarea", { value: changeDescription, onChange: (e) => setChangeDescription(e.target.value), rows: 2, className: "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent", placeholder: "Describe what changed in this deployment..." }), _jsx("button", { onClick: () => setShowDeployConfirm(true), disabled: hasValidationErrors || deploying, className: "w-full px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed", children: deploying ? 'Deploying...' : 'Deploy to Production' })] }), activeDeployment && (_jsx("button", { onClick: () => setShowRollbackConfirm(true), disabled: deploying, className: "w-full px-4 py-2 border border-orange-300 text-orange-700 rounded-md text-sm font-medium hover:bg-orange-50 disabled:opacity-50", children: "Rollback to Previous Version" })), deployments.length > 0 && (_jsxs("div", { children: [_jsx("h3", { className: "text-sm font-medium text-gray-700 mb-3", children: "Deployment History" }), _jsx("div", { className: "space-y-2 max-h-64 overflow-y-auto", children: deployments.map((d) => (_jsxs("div", { className: "flex items-center justify-between p-3 bg-white border rounded-md text-sm", children: [_jsxs("div", { children: [_jsxs("span", { className: "font-medium text-gray-900", children: ["v", d.version] }), _jsx("span", { className: "text-gray-500 ml-2", children: new Date(d.startedAt).toLocaleString() })] }), _jsx(StatusBadge, { status: statusBadgeMap[d.status] || 'warning', label: d.status.replace('_', ' ') })] }, d.id))) })] })), _jsx(ConfirmDialog, { isOpen: showDeployConfirm, title: "Deploy Workflow", message: `Deploy version ${currentVersion} to production? This will replace the currently active version.`, confirmLabel: "Deploy", cancelLabel: "Cancel", variant: "primary", onConfirm: handleDeploy, onCancel: () => setShowDeployConfirm(false) }), _jsx(ConfirmDialog, { isOpen: showRollbackConfirm, title: "Rollback Deployment", message: "Rollback to the previous active version? The current deployment will be deactivated.", confirmLabel: "Rollback", cancelLabel: "Cancel", variant: "danger", onConfirm: handleRollback, onCancel: () => setShowRollbackConfirm(false) })] }));
};
export default WorkflowDeploymentPanel;
